/**
 * -----------------------------------------------------------------------------
 * GROUPED_RECHARGE_TASK — 前端约定（非单独业务模块；list / popupItems 语义见下）
 *
 * • 展示「待充值档位 / 浮窗绑定的任务行」时用 `activities.GROUPED_RECHARGE_TASK.list`，
 *   **产品语义上**只关心待生效任务（通常为 PENDING），由服务端 control list；前端不在此层再筛档位。
 *
 * • 到账确认（弹窗到货条 / Bonus Ready / popup read）：仅以根级 **popupItems** + **popup/read**
 *   （及同源本地已读）为准；**不要**拿 list.paid 或行间领取状态当到账依据。
 *   到货条：经全站 **PopupQueue** 串行（一次只一个）、**仅首页**入队/展示；`useGroupedRechargeClaimPopup` 只认「popupItems 里未读」按 **ledgerId 优先 / claimId 兜底** 去重，
 *   避免与合并卡用的 list/root 标志不同步时出现第二次成功仍不弹出。
 *
 * • `list` 为空但仍有 `popupItems`：仅代表「到账确认」、无充值档位——正常。
 * -----------------------------------------------------------------------------
 */

/** 与后端对齐：根级、任意 list 行上的 bonusPopupPending */
function resolveGroupedBonusPopupPendingFlag(data: {
  bonusPopupPending?: unknown;
  list?: unknown;
}): boolean {
  const bpp = data?.bonusPopupPending;
  if (bpp === true || bpp === 1 || String(bpp ?? "").toLowerCase() === "true")
    return true;
  const rows = data?.list;
  if (!Array.isArray(rows)) return false;
  return rows.some((row: any) => {
    const p = row?.bonusPopupPending;
    return p === true || p === 1 || String(p ?? "").toLowerCase() === "true";
  });
}

/**
 * GROUPED_RECHARGE_TASK：到账/Bonus Ready 以 bonusPopupPending + popupItems / popupRewardAmount，
 * 不要用 list 里的 paid、claimStatus。
 * 若后端只在 list[] 上下发 bonusPopupPending、根对象无该字段，仍可通过非空 popupItems（含 claimId）判定为待弹窗。
 */
export function isGroupedBonusPopupPending(data: {
  bonusPopupPending?: unknown;
  popupItems?: unknown;
  popupRewardAmount?: unknown;
  list?: unknown;
}): boolean {
  const items = data?.popupItems;
  if (Array.isArray(items) && items.some((x: any) => x?.claimId)) {
    return true;
  }

  const pending = resolveGroupedBonusPopupPendingFlag(data);
  if (!pending) return false;

  const pra = Number(data?.popupRewardAmount ?? NaN);
  if (Number.isFinite(pra) && pra > 0) return true;

  const rows = data?.list;
  if (Array.isArray(rows)) {
    const sumListPopup = rows.reduce(
      (s, row: any) => s + Number(row?.popupRewardAmount ?? 0),
      0
    );
    if (sumListPopup > 0) return true;
  }

  return false;
}

/** Bonus Ready 卡片上展示金额：优先 popupRewardAmount，其次汇总 popupItems，最后回退 list 合计 */
export function getGroupedBonusDisplayAmount(
  taskData: any,
  listTotalFallback: number
): number {
  const pra = Number(taskData?.popupRewardAmount ?? NaN);
  if (Number.isFinite(pra) && pra > 0) return pra;
  const items = taskData?.popupItems;
  if (Array.isArray(items) && items.length > 0) {
    const s = items.reduce(
      (sum, it: any) => sum + Number(it?.rewardAmount ?? 0),
      0
    );
    if (s > 0) return s;
  }
  const rows = taskData?.list;
  if (Array.isArray(rows)) {
    const sumList = rows.reduce(
      (sum, row: any) => sum + Number(row?.popupRewardAmount ?? 0),
      0
    );
    if (sumList > 0) return sumList;
  }
  return listTotalFallback;
}
